import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { TableModule } from 'primeng/table';
import { SkeletonModule } from 'primeng/skeleton';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { DrawerModule } from 'primeng/drawer';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { ActivatedRoute, Router } from '@angular/router';
import { InteractionService, IInteraction } from '../../../core/services/interaction.service';
import { ReservationService, Reservation } from '../../../core/services/reservation.service';
import { MessageService, ConfirmationService } from 'primeng/api';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ToastModule } from 'primeng/toast';
import { CustomConfirmModalComponent } from '../../../shared/components/custom-confirm-modal/custom-confirm-modal.component';
import { AuthService } from '../../../core/services/auth';
import { FileIconService, FileIconInfo } from '../../../core/services/file-icon.service';

@Component({
  selector: 'app-view-reservation',
  imports: [ButtonModule, CardModule, TableModule, CommonModule, SkeletonModule, TagModule, ToastModule, TooltipModule, DrawerModule, ConfirmDialogModule, ProgressSpinnerModule, CustomConfirmModalComponent],
  templateUrl: './view-reservation.html',
  styleUrl: './view-reservation.scss',
  providers: [MessageService, ConfirmationService]
})
export class ViewReservation implements OnInit {
  reservationId: string = '';
  interactions: IInteraction[] = [];
  isLoading: boolean = true;
  isLoadingReservation: boolean = true;
  loadingError: string | null = null;
  reservationError: string | null = null;
  reservationData: any = {
    customerName: 'Loading...',
    reservationNumber: 'Loading...',
    refNo: '',
    products: [],
    validTill: 'Loading...',
    customerId: '',
    status: ''
  };

  // Drawer properties
  isDrawerVisible: boolean = false;
  selectedInteraction: IInteraction | null = null;

  // Custom modal properties for upcoming interactions
  showUpcomingInteractionsModal: boolean = false;
  upcomingInteractionsMessage: string = '';
  pendingUpcomingInteractions: IInteraction[] = [];

  // Current user context
  currentUser: any = null;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private interactionService: InteractionService,
    private reservationService: ReservationService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private authService: AuthService,
    private fileIconService: FileIconService
  ) {}

  ngOnInit(): void {
    console.log('ViewReservation component initialized');
    // Load current user context
    this.currentUser = this.authService.getLoggedInUser();
    this.getReservationIdFromUrl();
  }

  // Check if current user is SA or A (should not have access to certain actions)
  isSAOrA(): boolean {
    const roleIdentification = this.currentUser?.roleId?.identification || this.currentUser?.role?.identification;
    return roleIdentification === 'SA' || roleIdentification === 'A';
  }

  getReservationIdFromUrl(): void {
    this.route.params.subscribe(params => {
      this.reservationId = params['id'];
      console.log('Reservation ID from URL:', this.reservationId);
      if (this.reservationId) {
        this.loadReservationData();
        this.loadInteractions();
      }
    });
  }

  loadReservationData(): void {
    this.isLoadingReservation = true;
    console.log('Loading reservation data for ID:', this.reservationId);
    
    this.reservationService.getReservationById(this.reservationId).subscribe({
      next: (response: any) => {
        console.log('Reservation response:', response);
        if (response.success && response.data) {
          const reservation = response.data;
          this.reservationData = {
            customerName: `${reservation.customerId?.businessName?.substring(0, 40) || 'Unknown'}`,
            reservationNumber: reservation.refNo || reservation._id || 'Unknown',
            refNo: reservation.refNo || reservation._id || 'Unknown',
            products: reservation.products?.map((product: any) => product.name) || [],
            validTill: reservation.expiryDate ? new Date(reservation.expiryDate).toLocaleDateString('en-US', {
              year: 'numeric',
              month: 'short',
              day: 'numeric'
            }) : 'No expiry date',
            customerId: reservation.customerId?._id || reservation.customerId,
            status: reservation.status || ''
          };
        }
        this.isLoadingReservation = false;
        console.log('Reservation data loaded:', this.reservationData);
      },
      error: (error) => {
        console.error('Error loading reservation data:', error);
        this.reservationError = 'Failed to load reservation data. Please try refreshing the page.';
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to load reservation data'
        });
        this.isLoadingReservation = false;
      }
    });
  }

  loadInteractions(): void {
    if (this.reservationId) {
      this.isLoading = true;
      console.log('Loading interactions for reservation:', this.reservationId);
      
      this.interactionService.getInteractionsByReservationId(this.reservationId).subscribe({
        next: (response) => {
          console.log('Raw response:', response);
          this.interactions = response || [];
          this.isLoading = false;
          console.log('Interactions loaded:', this.interactions);
          console.log('Interactions type:', typeof this.interactions);
          console.log('Interactions length:', this.interactions?.length);
        },
        error: (error) => {
          console.error('Error loading interactions:', error);
          this.loadingError = 'Failed to load interactions. Please try refreshing the page.';
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to load interactions'
          });
          this.isLoading = false;
        }
      });
    }
  }

  retryLoading(): void {
    this.loadingError = null;
    this.reservationError = null;
    if (this.reservationId) {
      this.loadReservationData();
      this.loadInteractions();
    }
  }

  getStatusTag(interaction: IInteraction): { severity: string, value: string } {
    if (interaction.interactionStatus === 'upcoming') {
      return { severity: 'warning', value: 'Upcoming' };
    } else if (interaction.interactionStatus === 'completed') {
      return { severity: 'success', value: 'Completed' };
    }
    return { severity: 'info', value: interaction.interactionStatus };
  }

  getStatusColor(interaction: IInteraction): string {
    if (interaction.interactionStatus === 'upcoming') {
      return 'purple';
    } else if (interaction.interactionStatus === 'completed') {
      return 'green';
    }
    return 'gray';
  }

  formatDate(dateString: string | Date): string {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  }

  formatTime(timeString: string): string {
    if (!timeString) return 'N/A';
    
    // Convert 24-hour format to 12-hour format with AM/PM
    const [hours, minutes] = timeString.split(':');
    const hour = parseInt(hours, 10);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
    
    return `${displayHour}:${minutes} ${ampm}`;
  }

  getInteractionNotes(interaction: IInteraction): string {
    return interaction.interactionNotes || 'N/A';
  }

  onCompleteInteraction(interaction: IInteraction): void {
    // Navigate to complete interaction page or open modal
    this.router.navigate(['/admin/log-interaction/update', interaction._id]);
    this.messageService.add({
      severity: 'info',
      summary: 'Info',
      detail: 'Redirecting to complete interaction...'
    });
  }

  onViewVisitInfo(interaction: IInteraction): void {
    this.selectedInteraction = interaction;
    this.isDrawerVisible = true;
  }

  onCloseDrawer(): void {
    this.isDrawerVisible = false;
    this.selectedInteraction = null;
  }

  getInteractionTypeDisplay(interaction: IInteraction): string {
    if (!interaction.interactionType) return 'N/A';
    return interaction.interactionType.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase());
  }

  downloadDocument(documentUrl: string): void {
    if (!documentUrl) return;

    // Try opening in a new tab (may be blocked by popup blockers)
    const newWindow = window.open(documentUrl, '_blank', 'noopener');
    
    // Fallback to anchor method if window.open was blocked
    if (!newWindow) {
      const anchor = document.createElement('a');
      anchor.href = documentUrl;
      anchor.target = '_blank';
      anchor.rel = 'noopener';
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
    }
  }

  getFileIcon(fileUrl: string): FileIconInfo {
    return this.fileIconService.getFileIcon(fileUrl);
  }

  onCustomerPage(): void {
    if (!this.reservationData.customerId) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Customer ID not found. Cannot navigate to customer.'
      });
      return;
    }
    this.router.navigate(['/admin/view-customer', this.reservationData.customerId]);
  }

  onScheduleNextInteraction(): void {
    this.router.navigate(['/admin/log-interaction/create', this.reservationId]);
  }

  onCloseReservation(): void {
    // 1) Block closing if there are upcoming interactions
    const upcomingInteractions = this.interactions.filter(
      interaction => interaction.interactionStatus === 'upcoming'
    );

    if (upcomingInteractions.length > 0) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Cannot Close Reservation',
        detail: `You have ${upcomingInteractions.length} upcoming interaction(s). Complete them before closing.`,
        life: 5000
      });
      return;
    }

    // 2) Ask for confirmation if no upcoming interactions
    this.confirmationService.confirm({
      header: 'Confirm Close',
      message: 'Are you sure you want to close this reservation? This action cannot be undone.',
      icon: 'pi pi-exclamation-triangle',
      rejectButtonStyleClass: 'p-button-outlined p-button-secondary',
      acceptLabel: 'Yes, Close',
      rejectLabel: 'Cancel',
      accept: () => this.proceedCloseReservation()
    });
  }

  onConvertToContract(): void {
    // Check if there are upcoming interactions
    const upcomingInteractions = this.interactions.filter(
      interaction => interaction.interactionStatus === 'upcoming'
    );

    if (upcomingInteractions.length > 0) {
      // Show popup with upcoming interactions and option to mark all as completed
      this.showUpcomingInteractionsPopup(upcomingInteractions);
    } else {
      // All interactions are completed, proceed to contract creation
      this.proceedToContractCreation();
    }
  }

  private showUpcomingInteractionsPopup(upcomingInteractions: IInteraction[]): void {
    const interactionList = upcomingInteractions.map(interaction => 
      `• ${this.getInteractionTypeDisplay(interaction)} on ${this.formatDate(interaction.interactionDate)} at ${this.formatTime(interaction.interactionTime)}`
    ).join('\n');

    this.upcomingInteractionsMessage = `You have ${upcomingInteractions.length} upcoming interaction(s) that need to be completed before converting to contract:\n\n${interactionList}\n\nWould you like to mark all interactions as completed and proceed to contract creation?`;
    this.pendingUpcomingInteractions = upcomingInteractions;
    this.showUpcomingInteractionsModal = true;
  }

  onConfirmUpcomingInteractions(): void {
    this.markAllInteractionsCompletedAndCreateContract(this.pendingUpcomingInteractions);
  }

  onCancelUpcomingInteractions(): void {
    this.showUpcomingInteractionsModal = false;
    this.pendingUpcomingInteractions = [];
  }

  private markAllInteractionsCompletedAndCreateContract(upcomingInteractions: IInteraction[]): void {
    if (!this.reservationId) {
      console.error('Reservation ID is not available');
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Reservation ID not found. Cannot mark interactions as completed.'
      });
      return;
    }

    this.isLoading = true;
    
    this.interactionService.updateAllInteractionsToCompleted(this.reservationId).subscribe({
      next: (response) => {
        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: `${response.modifiedCount || upcomingInteractions.length} interactions marked as completed!`
        });
        
        // Reload interactions to reflect the changes
        this.loadInteractions();
        
        // Close the modal and proceed to contract creation
        this.showUpcomingInteractionsModal = false;
        this.pendingUpcomingInteractions = [];
        this.proceedToContractCreation();
      },
      error: (error) => {
        console.error('Error updating interactions:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to mark interactions as completed. Please try again.'
        });
        this.isLoading = false;
      }
    });
  }

  private proceedToContractCreation(): void {
    // Navigate to contract creation page with reservationId
    this.router.navigate(['/admin/add-edit-contract', this.reservationId]);
  }

  private proceedCloseReservation(): void {
    if (!this.reservationData.customerId) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Customer ID not found. Cannot close reservation.'
      });
      return;
    }

    // Check for upcoming interactions before closing
    const upcomingInteractions = this.interactions.filter(interaction => 
      interaction.interactionStatus === 'upcoming'
    );

    if (upcomingInteractions.length > 0) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Cannot Close Reservation',
        detail: `You have ${upcomingInteractions.length} upcoming interaction(s) for this reservation. Please complete all upcoming interactions before closing the reservation.`,
        life: 5000
      });
      return;
    }

    // Update reservation status to "Not Interested"
    this.reservationService.updateReservationStatus(this.reservationId, 'Not Interested').subscribe({
      next: (response) => {
        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: 'Reservation closed successfully!'
        });

        // Redirect to view-customer page after a short delay
        setTimeout(() => {
          this.router.navigate(['/admin/view-customer', this.reservationData.customerId]);
        }, 1500);
      },
      error: (error) => {
        console.error('Error closing reservation:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to close reservation. Please try again.'
        });
      }
    });
  }
}
